import React from "react";
import { Box, Typography } from "@mui/material";
import {
  C, NumberStepper, ChoiceCard, Segmented,
} from "./staffingUi";
import {
  LEVELS, DEFAULT_LIMIT, MAX_LIMIT, MAX_OPENINGS,
  roleTitle, roleHint, sessionLabel, formatSessionDate, reviewRows, toggleLevel,
} from "./staffingModel";

const heading = (text) => (
  <Typography component="h3" sx={{ m: "0 0 4px", fontSize: 15, fontWeight: 600 }}>{text}</Typography>
);
const lead = (text) => (
  <Typography sx={{ m: "0 0 16px", fontSize: 13, color: C.muted }}>{text}</Typography>
);
const errorText = (text) => text && (
  <Typography role="alert" sx={{ m: "12px 0 0", fontSize: 12.5, color: C.danger }}>{text}</Typography>
);

export function OpeningsStep({ w, set, session, sessionCount, rangeText, roles, error }) {
  return (
    <>
      {heading("Which sessions?")}
      {lead("Instructors will see the opening on their Teaching Schedule.")}
      <Box role="radiogroup" aria-label="Sessions"
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "10px", mb: "20px" }}>
        <ChoiceCard checked={w.scope === "one"} title={`${sessionLabel(session)} only`}
          description={formatSessionDate(session)} testId="scope-one" onClick={() => set({ scope: "one" })} />
        <ChoiceCard checked={w.scope === "all"} title={`All ${sessionCount} upcoming sessions`}
          description={`Whole class · ${rangeText}`} testId="scope-all" onClick={() => set({ scope: "all" })} />
      </Box>

      {heading("How many instructors do you need?")}
      {lead(`${w.scope === "all" ? "Openings per session. " : ""}Already-assigned instructors aren’t counted.`)}
      {roles.map((role, i) => (
        <Box key={role} sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "12px", alignItems: "center",
          py: "12px", borderTop: i ? `1px solid ${C.line}` : 0 }}>
          <Box>
            <Box component="b" sx={{ display: "block", fontWeight: 600 }}>{roleTitle(role)}</Box>
            <Box component="small" sx={{ fontSize: 12.5, color: C.muted }}>{roleHint(role)}</Box>
          </Box>
          <NumberStepper label={roleTitle(role)} value={w.counts[role] || 0} min={0} max={MAX_OPENINGS}
            onChange={(n) => set({ counts: { ...w.counts, [role]: n } })} />
        </Box>
      ))}
      {errorText(error)}
    </>
  );
}

export function MethodStep({ w, set }) {
  const limited = w.limit != null;
  return (
    <>
      {heading("How should instructors sign up?")}
      {lead("You can change this later.")}
      <Box role="radiogroup" aria-label="Sign-up method" sx={{ display: "grid", gap: "10px" }}>
        <ChoiceCard checked={w.mode === "APPROVAL"} title="Request and approval" testId="mode-approval"
          description="Instructors request the opening. You review each request and choose who teaches."
          onClick={() => set({ mode: "APPROVAL" })} />
        {w.mode === "APPROVAL" && (
          <Box sx={{ m: "2px 0 6px 30px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontSize: 13 }}>
            <Segmented label="Request limit" value={limited ? "n" : "unl"}
              options={[["unl", "Unlimited requests"], ["n", "Set a limit"]]}
              onChange={(k) => set({ limit: k === "unl" ? null : w.max })} />
            {limited && (
              <>
                <NumberStepper label="requests" value={w.limit} min={1} max={MAX_LIMIT}
                  onChange={(n) => set({ limit: n, max: n })} />
                <span style={{ color: "var(--color-accent-violet-mid)" }}>requests, then it closes</span>
              </>
            )}
          </Box>
        )}
        <ChoiceCard checked={w.mode === "FIRST_COME"} title="First come, first served" testId="mode-first-come"
          description="The first eligible instructors to sign up are assigned automatically. No review needed."
          onClick={() => set({ mode: "FIRST_COME", max: w.max || DEFAULT_LIMIT })} />
      </Box>
    </>
  );
}

export function LevelsStep({ w, set, error }) {
  return (
    <>
      {heading("Who can request this?")}
      {lead("Only members at these levels will see the opening.")}
      <Box sx={{ display: "grid", gap: "10px" }}>
        {LEVELS.map((l) => (
          <ChoiceCard key={l.key} kind="checkbox" checked={w.levels.includes(l.key)} title={l.title}
            description={l.desc} testId={`level-${l.key.toLowerCase()}`}
            onClick={() => set({ levels: toggleLevel(w.levels, l.key) })} />
        ))}
      </Box>
      {errorText(error)}
    </>
  );
}

export function ReviewStep({ w, session, sessionCount, goTo }) {
  const rows = reviewRows(w, { session, sessionCount });
  const needed = Object.entries(w.counts).filter(([, n]) => n > 0).map(([role]) => roleTitle(role));
  return (
    <>
      {heading("Review and post")}
      {lead("Check the details. Instructors are notified when you post.")}
      <Box component="dl" sx={{ m: 0, border: `1px solid ${C.line}`, borderRadius: "12px" }}>
        {rows.map((r, i) => (
          <Box key={r.label} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr auto", sm: "150px minmax(0,1fr) auto" },
            gap: "12px", p: "12px 14px", fontSize: 13.5, alignItems: "start", borderTop: i ? `1px solid ${C.line}` : 0 }}>
            <Box component="dt" sx={{ color: C.muted, fontSize: 13, gridColumn: { xs: "1 / -1", sm: "auto" } }}>{r.label}</Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 500 }}>{r.value}</Box>
            <Box component="button" type="button" aria-label={`Edit ${r.label}`} onClick={() => goTo(r.step)}
              sx={{ border: 0, bgcolor: "transparent", color: C.violet, font: "600 12.5px var(--font-ui)", cursor: "pointer", p: 0 }}>
              Edit
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: "16px", p: "14px", borderRadius: "12px", bgcolor: "#fafafb", fontSize: 13 }}>
        <Box component="span" sx={{ display: "block", mb: "8px", font: "600 10.5px var(--font-ui)", letterSpacing: ".5px",
          textTransform: "uppercase", color: C.muted }}>What instructors see</Box>
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: "8px", p: "12px", display: "flex",
          alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <Box component="b" sx={{ flex: 1, minWidth: 160, fontWeight: 600 }}>
            {w.scope === "all" ? "Whole class" : sessionLabel(session)}
          </Box>
          {needed.map((t) => (
            <Box key={t} component="span" sx={{ display: "inline-flex", alignItems: "center", height: 22, px: "7px", borderRadius: "4px",
              bgcolor: C.hover, font: "600 10.5px var(--font-ui)", letterSpacing: ".3px", textTransform: "uppercase" }}>
              {t} needed
            </Box>
          ))}
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", height: 30, px: "10px", borderRadius: "8px",
            bgcolor: C.primary, color: "#fff", font: "600 11px var(--font-ui)", letterSpacing: ".2px", textTransform: "uppercase" }}>
            {w.mode === "FIRST_COME" ? "Sign up" : "Request to teach"}
          </Box>
        </Box>
      </Box>
    </>
  );
}
